import type { CompanyDetails, LegalDocument } from '@london-life/contracts';

// The text of the three legal documents. Drafts for Jerry's review (and ideally a solicitor's) before
// they are final. Changing a document's version in
// packages/contracts/src/profile.ts asks every player to accept it again.
export type Section = { heading: string; body: (string | string[])[] };
// the documents for the company details the super admin keeps in the dashboard
export function documentsFor(company: CompanyDetails): Record<LegalDocument, { intro: string; sections: Section[] }> {
  const who = `${company.companyName}, a company registered in England and Wales (company number ${company.companyNumber}), registered office ${company.registeredOffice}`;
  return {
    terms: {
      intro:
        'These terms are the agreement between you and us for using London Life. Please read them: by creating an account you agree to them.',
      sections: [
        {
          heading: '1. Who we are',
          body: [`London Life is run by ${who} ("we", "us"). You can contact us at ${company.supportEmail}.`],
        },
        {
          heading: '2. Who can play',
          body: [
            'You must be 18 or over. We ask for your date of birth once when you join; if it shows you are under 18 we block the account. Giving a false date of birth breaks these terms.',
            'You may have one account. Keep your sign-in details private: you are responsible for what happens on your account.',
          ],
        },
        {
          heading: '3. The game and its money',
          body: [
            'London Life is a game. Its pounds and pence, homes, jobs, items and everything else in it are virtual: they have no value outside the game and cannot be exchanged for real money, goods or services, or transferred to anyone outside the game.',
            'You get a licence to use virtual items while your account is active; you do not own them. We may change, rebalance or reset the game, its economy and its items to keep it fair and working, and we will tell you about big changes in advance where we can.',
            'If we ever sell anything for real money, the price and what you get will be shown before you pay, and your rights as a UK consumer are not affected.',
          ],
        },
        {
          heading: '4. How to behave',
          body: [
            'London Life is for everyone who lives in, or comes to, the UK. Treat other players with respect. You must not:',
            [
              'harass, bully, threaten or abuse anyone, or post hate speech about anyone’s race, ethnicity, nationality, religion, sex, gender, sexuality, disability or age;',
              'share sexual content, or anything involving or aimed at children;',
              'impersonate other people, players or our staff, or use a misleading username;',
              'cheat, use bots or scripts, exploit bugs, or sell or buy accounts or virtual items for real money;',
              'share other people’s personal information, spam, scam or advertise without our permission;',
              'break the law or encourage others to.',
            ],
          ],
        },
        {
          heading: '5. What you post',
          body: [
            'You keep ownership of what you write in chat and on your profile, and you give us permission to show it in the game and to keep it for safety and moderation. Do not post anything you do not have the right to share.',
            'Players can report others. We may review, remove or hide content and messages that break these terms.',
          ],
        },
        {
          heading: '6. If these terms are broken',
          body: [
            'We may warn you, mute you, remove items or progress gained unfairly, suspend your account or close it, depending on how serious it is. If you think we got it wrong, write to us and a person will review it.',
          ],
        },
        {
          heading: '7. Closing your account',
          body: [
            'You can close your account at any time from your settings (or by writing to us). We may close accounts that break these terms, or end the game with reasonable notice.',
          ],
        },
        {
          heading: '8. Our responsibility to you',
          body: [
            'We work to keep London Life running, safe and fair, but it is provided as it is and may sometimes be unavailable or contain mistakes.',
            'Nothing in these terms limits our liability for death or personal injury caused by our negligence, for fraud, or for anything else that cannot be limited by law. Otherwise we are not responsible for losses that were not foreseeable, for business losses, or for the loss of virtual items.',
          ],
        },
        {
          heading: '9. Changes to these terms',
          body: [
            'If we change these terms we will update the date at the top and ask you to accept the new version before you carry on playing. If you do not agree you can close your account.',
          ],
        },
        {
          heading: '10. The law',
          body: [
            'These terms are governed by the law of England and Wales, and the courts of England and Wales can hear any dispute, though if you live in Scotland or Northern Ireland you can also bring proceedings there.',
          ],
        },
      ],
    },
    privacy: {
      intro:
        'This policy explains what personal data London Life collects, why, who we share it with, how long we keep it and your rights under UK data protection law (the UK GDPR and the Data Protection Act 2018).',
      sections: [
        {
          heading: '1. Who is responsible for your data',
          body: [
            `${who} is the controller of your personal data${company.icoNumber ? ` (ICO registration ${company.icoNumber})` : ''}. Contact us about your data at ${company.privacyEmail}.`,
          ],
        },
        {
          heading: '2. What we collect',
          body: [
            [
              'Account: your email address, the name on your Google account or the name you give us, and a sign-in identifier from Google Firebase (we never see or store your password).',
              'Profile: your @username and your date of birth. If your date of birth shows you are under 18 we delete it and keep only the fact that the account was blocked.',
              'Your agreement: which versions of these documents you accepted, when, and from which internet address.',
              'Game: your character, progress, money, homes, items, and the messages you send in the game.',
              'Technical: your internet address, browser and device details, and records of what happened on our servers, used for security and to fix problems.',
              'Visitor counts: how many people are online now, and how many different people have visited since the game opened. While the game is open, your browser tells our server once a minute that it is still there. To count you, the server turns your internet address into scrambled codes with a secret, and never keeps the address. "Online now" is the number of codes, made with a secret that changes every day and kept for at most two days, seen in the last two minutes. "Visitors" is the number of different codes, made with a fixed secret, ever seen: that code is added to a count that cannot be turned back into the code or the address, so we cannot tell who visited, only how many. People who share an internet connection count as one. No cookie is used and nothing is stored on your device for this.',
            ],
          ],
        },
        {
          heading: '3. Why we use it, and our legal basis',
          body: [
            [
              'To run your account and the game you signed up for (performance of our contract with you).',
              'To check you are 18 or over, keep the game safe, prevent fraud and cheating, moderate chat and keep records of what staff do (our legitimate interests in a safe, fair game, and legal obligations).',
              'To answer you when you contact us (our legitimate interests).',
              'Analytics and any non-essential cookies only with your consent, which you can withdraw at any time. Until we ask, we do not use any.',
            ],
          ],
        },
        {
          heading: '4. Cookies and similar technology',
          body: [
            'We use a cookie to keep you signed in and to protect your account against forged requests, and Google Firebase stores sign-in details in your browser. These are strictly necessary for the game to work. We will ask for your consent before adding any analytics cookies.',
          ],
        },
        {
          heading: '5. Who we share it with',
          body: [
            'We do not sell your data. We use these providers to run the game, under contracts that require them to protect it:',
            [
              'Google (Firebase Authentication): sign-in.',
              'Cloudflare: website delivery, protection against attacks, and encrypted backup storage.',
              'Hetzner Online: the servers that run the game, in the European Union.',
              'Purelymail: our email.',
            ],
            'Some of these process data outside the UK (for example in the USA or the EU). Where they do, the transfer is protected by UK adequacy regulations or the UK’s approved contract terms. We may also share data where the law requires it, for example with the police.',
          ],
        },
        {
          heading: '6. How long we keep it',
          body: [
            [
              'Account, profile and game data: while your account is open. You can delete your account at any time from Account, Delete my account: we erase your personal details straight away, and keep records of in-game money movements without your name, so the game’s accounts still balance.',
              'Server records: 30 days. Encrypted backups: up to 35 days.',
              'Records of what staff did and of document acceptances: kept for as long as we need them to show we acted properly, usually six years.',
            ],
          ],
        },
        {
          heading: '7. How we protect it',
          body: [
            'Data travels encrypted, staff access is limited to what each role needs and needs two-step login, sensitive secrets are encrypted, backups are encrypted before they leave our server, and every staff action is recorded.',
          ],
        },
        {
          heading: '8. Your rights',
          body: [
            `You can ask us for a copy of your data, to correct it, to delete it, to restrict or object to how we use it, and to receive it in a portable form. Write to ${company.privacyEmail}; we reply within one month. You can also complain to the Information Commissioner’s Office (ico.org.uk), though we would like the chance to help first.`,
          ],
        },
        {
          heading: '9. Children',
          body: [
            'London Life is only for adults. We do not knowingly collect data from anyone under 18; if we learn we have, we delete it.',
          ],
        },
        {
          heading: '10. Changes',
          body: [
            'If we change this policy we will update the date at the top and tell you in the game before it applies.',
          ],
        },
      ],
    },
    disclaimer: {
      intro: 'Please read this before playing.',
      sections: [
        {
          heading: 'It is a game',
          body: [
            'London Life is a game set in a fictional version of the UK. Places are inspired by real cities, areas and streets, but the people, businesses, events and stories in it are made up. Any likeness to real people is a coincidence. Real place names do not mean those places or their owners are connected to us or endorse the game.',
          ],
        },
        {
          heading: 'Not real money, not real advice',
          body: [
            'Money, wages, rents, taxes, prices, benefits, visas, studies, jobs and legal processes in the game are simplified so the game is fun to play. They are not accurate guides to real life and are not financial, legal, tax, immigration or career advice. For real decisions, use official sources such as GOV.UK or a qualified adviser.',
          ],
        },
        {
          heading: 'What players say',
          body: [
            'Players’ messages and profiles are their own words, not ours. We moderate and act on reports, but we do not check everything before it appears. Never share personal details, passwords or money with anyone you meet in the game.',
          ],
        },
        {
          heading: 'Other websites and adverts',
          body: [
            'The game may link to other websites or show adverts. We are not responsible for their content, products or services.',
          ],
        },
        {
          heading: 'Adults only',
          body: ['London Life is for people aged 18 and over.'],
        },
      ],
    },
  };
}
